import c from 'tinyrainbow';
import { resolve, join, dirname } from 'node:path';
import { mkdirSync, writeFileSync, unlinkSync, existsSync, readFileSync } from 'node:fs';
import { tmpdir } from 'node:os';
import { fileURLToPath } from 'node:url';
import path from 'node:path';
import tsconfigPaths from 'vite-tsconfig-paths';

import { createVitest, registerConsoleShortcuts } from 'vitest/node';
import type { TestRunResult } from 'vitest/node';
import { AxiomReporter } from './reporter';
import { flush, initInstrumentation } from './instrument';
import { setAxiomConfig } from './context/storage';
import type { ResolvedAxiomConfig } from '../config/index';

const VITEST_MODE = 'eval';

const getCurrentDir = (): string => {
  if (typeof __dirname !== 'undefined') {
    return __dirname;
  }

  return path.dirname(fileURLToPath(import.meta.url));
};

const evalsRunnerPath = resolve(getCurrentDir(), 'evals', 'custom-runner.js');

export const resolveVitestConfigPath = (
  vitestConfig: ResolvedAxiomConfig['eval']['vitestConfig'],
  configPath?: string,
  rootDir?: string,
): string | false => {
  if (typeof vitestConfig !== 'string') {
    return false;
  }

  return resolve(configPath ? dirname(configPath) : rootDir || process.cwd(), vitestConfig);
};

const printCollectedEvals = (result: TestRunResult, rootDir: string) => {
  if (!result.testModules || result.testModules.length === 0) {
    console.log(c.yellow('\nNo evaluations found\n'));
    return;
  }

  console.log(c.bold('\nFound evaluations:\n'));

  let totalEvals = 0;
  let totalCases = 0;

  for (const module of result.testModules) {
    const relativePath = path.relative(rootDir, module.moduleId);

    for (const suite of module.children.suites()) {
      totalEvals++;
      const caseCount = suite.children.size;
      totalCases += caseCount;
      console.log(c.green(`✓ ${suite.name} (${caseCount} cases)`));
      console.log(c.dim(`  ${relativePath}`));
      console.log('');
    }
  }

  console.log(c.bold(`Total: ${totalEvals} evaluations, ${totalCases} test cases\n`));
};

const hasVitestFailures = (
  result: TestRunResult,
  failedTestCount: number,
  dangerouslyIgnoreUnhandledErrors: boolean,
): boolean => {
  if (failedTestCount > 0) {
    return true;
  }

  if (result.unhandledErrors.length > 0 && !dangerouslyIgnoreUnhandledErrors) {
    return true;
  }

  return result.testModules.some(
    (testModule) => testModule.state() === 'failed' || !testModule.ok(),
  );
};

export const runVitest = async (
  dir: string,
  opts: {
    watch: boolean;
    baseline?: string;
    include: string[];
    exclude?: string[];
    testNamePattern?: RegExp;
    debug?: boolean;
    list?: boolean;
    overrides?: Record<string, any>;
    config: ResolvedAxiomConfig;
    configPath?: string;
    runId: string;
    consoleUrl?: string;
  },
) => {
  process.env.AXIOM_EVAL = 'true';

  // Store config globally so reporters can access it
  setAxiomConfig(opts.config);
  // Initialize instrumentation explicitly based on debug or list flag
  await initInstrumentation({
    enabled: !opts.debug && !opts.list,
    config: opts.config,
  });

  const providedConfig: ResolvedAxiomConfig = {
    ...opts.config,
    eval: {
      ...opts.config.eval,
      // These can't be serialized, so we need to remove them
      instrumentation: null,
      flagSchema: null,
    },
  };

  if (opts.debug) {
    console.log(c.bgWhite(c.blackBright(' Debug mode enabled ')));
  }

  // Setup temp files for cross-worker name validation
  const tmpDir = join(tmpdir(), 'axiom-eval', opts.runId);
  mkdirSync(tmpDir, { recursive: true });

  const nameRegistryFile = join(tmpDir, 'names.jsonl');
  const abortFile = join(tmpDir, 'abort.txt');

  // Clear registry file and remove any stale abort file
  writeFileSync(nameRegistryFile, '', 'utf8');
  if (existsSync(abortFile)) {
    unlinkSync(abortFile);
  }

  // Make paths available to workers and reporters
  process.env.AXIOM_NAME_REGISTRY_FILE = nameRegistryFile;
  process.env.AXIOM_ABORT_FILE = abortFile;

  if (opts.list) {
    console.log(c.bgWhite(c.blackBright(' List mode ')));
  }

  const evalTimeoutMs = opts.config?.eval?.timeoutMs || 60_000;
  const hookTimeoutMs = Math.max(evalTimeoutMs, 60_000);
  const vitestConfig = resolveVitestConfigPath(opts.config.eval.vitestConfig, opts.configPath, dir);

  const vi = await createVitest(
    'test',
    {
      config: vitestConfig,
      root: dir ? dir : process.cwd(),
      mode: VITEST_MODE,
      include: opts.include,
      exclude: opts.exclude,
      testNamePattern: opts.testNamePattern,
      reporters: ['verbose', new AxiomReporter()],
      environment: 'node',
      browser: undefined,
      watch: opts.watch,
      name: 'axiom:eval',
      printConsoleTrace: true,
      silent: false,
      disableConsoleIntercept: true,
      testTimeout: evalTimeoutMs,
      // Vitest default is 10s, which might not be enough to flush OTLP exporters and update the evaluation via API in `afterAll`
      hookTimeout: hookTimeoutMs,
      globals: true,
      runner: evalsRunnerPath,
      provide: {
        baseline: opts.baseline,
        debug: opts.debug,
        list: opts.list,
        overrides: opts.overrides,
        axiomConfig: providedConfig,
        runId: opts.runId,
        consoleUrl: opts.consoleUrl,
      },
      ...(vitestConfig === false ? { setupFiles: [] } : {}),
    },
    {
      plugins: [tsconfigPaths({ root: dir || process.cwd() })],
    },
  );

  // List mode: just list tests without running them
  if (opts.list) {
    const result = await vi.collect();
    printCollectedEvals(result, dir || process.cwd());
    await vi.close();
    process.exit(0);
  }

  // Start collection and execution
  const result = await vi.start();

  // After execution, check if validation failed
  if (existsSync(abortFile)) {
    const message = readFileSync(abortFile, 'utf8');
    console.error('\n' + message);
    await vi.close();
    process.exit(1);
  }

  const dispose = registerConsoleShortcuts(vi, process.stdin, process.stdout);

  if (!vi.shouldKeepServer()) {
    const exitCode = hasVitestFailures(
      result,
      vi.state.getCountOfFailedTests(),
      vi.config.dangerouslyIgnoreUnhandledErrors,
    )
      ? 1
      : 0;
    dispose();
    await flush();
    await vi.close();
    process.exit(exitCode);
  }

  await flush();
};
